<template>
    <div
        class="flex flex-col gap-2 transition-all relative z-20 w-full pb-2.5 search-container"
        :class="{ 'gap-5 mb-2': errors.length > 0 }"
    >
        <TransitionGroup>
            <span
                v-for="(trip, index) in localTrips"
                class="@container flex transition-all justify-center"
                :key="index"
            >
                <SearchBar :key="index" :loading="loading" class="w-full">
                    <template #inputs>
                        <div
                            class="w-full flex flex-col @2xl:flex-row justify-start items-start bg-white @2xl:mr-2 gap-2 md:gap-3"
                        >
                            <!-- START FROM -->
                            <div
                                class="w-full sm:flex-[2_2_0%] @2xl:h-10 px-4 py-2.5 bg-white justify-start items-center gap-2 flex border border-black border-opacity-0 rounded"
                                :class="{
                                    'ring-red-600 ring-1':
                                        errors[index]?.includes('from'),
                                }"
                            >
                                <!-- START ICON -->
                                <div
                                    class="w-5 h-5 justify-center items-center flex text-slate-800"
                                >
                                    <LoungeIcon icon="origin" />
                                </div>
                                <!-- END ICON -->

                                <LoungesAutoSuggest
                                    class="text-slate-500 text-sm font-normal leading-tight"
                                    placeholder="From"
                                    name="from"
                                    valueKey="code"
                                    displayKey="name"
                                    :id="`from-${index}`"
                                    :ref="`from-${index}`"
                                    :inputValue="localTrips[index].from?.name"
                                    :allSuggestions="airports"
                                    @select="
                                        (value) =>
                                            handleSelect(value, index, 'from')
                                    "
                                />
                                <SearchFormValidationErrors
                                    v-if="errors[index]?.length"
                                    :errors="errors[index]"
                                    errorType="from"
                                    errorMessage="Please select an airport"
                                    class="mt-5 left-0"
                                />
                            </div>
                            <!-- END FROM -->
                            <div
                                class="block w-full @2xl:w-px h-px @2xl:h-8 @2xl:my-auto bg-slate-200"
                            ></div>
                            <!-- START TO -->
                            <div
                                class="w-full sm:flex-[2_2_0%] @2xl:h-10 px-4 py-2.5 bg-white justify-start items-center gap-2 flex rounded relative"
                                :class="{
                                    'ring-red-600 ring-1':
                                        errors[index]?.includes('to'),
                                }"
                            >
                                <!-- START ICON -->
                                <div
                                    class="w-5 h-5 justify-center items-center flex text-slate-800"
                                >
                                    <LoungeIcon icon="destination" />
                                </div>
                                <!-- END ICON -->
                                <LoungesAutoSuggest
                                    class="text-slate-500 text-sm font-normal leading-tight"
                                    placeholder="To"
                                    name="to"
                                    valueKey="code"
                                    displayKey="name"
                                    :id="`to-${index}`"
                                    :ref="`to-${index}`"
                                    :inputValue="localTrips[index].to?.name"
                                    :allSuggestions="airports"
                                    @select="
                                        (value) =>
                                            handleSelect(value, index, 'to')
                                    "
                                />
                                <SearchFormValidationErrors
                                    v-if="errors[index]?.length"
                                    :errors="errors[index]"
                                    errorType="to"
                                    errorMessage="Please select an airport"
                                    class="mt-5 left-0"
                                />
                            </div>
                            <!-- END TO -->
                            <div
                                class="block w-full @2xl:w-px h-px @2xl:h-8 @2xl:my-auto bg-slate-200"
                            ></div>
                            <!-- START AIRLINE -->
                            <div
                                class="w-full sm:flex-[2_2_0%] @2xl:h-10 px-4 py-2.5 bg-white justify-start items-center gap-2 flex rounded relative"
                                :class="{
                                    'ring-red-600 ring-1':
                                        errors[index]?.includes('airline'),
                                }"
                            >
                                <!-- START ICON -->
                                <div
                                    class="w-5 h-5 justify-center items-center flex text-slate-800"
                                >
                                    <LoungeIcon icon="airline" />
                                </div>
                                <!-- END ICON -->
                                <LoungesAutoSuggest
                                    class="text-slate-500 text-sm font-normal leading-tight"
                                    placeholder="Airline"
                                    name="airline"
                                    :id="`airline-${index}`"
                                    :ref="`airline-${index}`"
                                    dropdownName="airline"
                                    :inputValue="
                                        localTrips[index].airline?.name
                                    "
                                    :allSuggestions="airlines"
                                    valueKey="code"
                                    displayKey="name"
                                    @select="
                                        (value) =>
                                            handleSelect(
                                                value,
                                                index,
                                                'airline'
                                            )
                                    "
                                />
                                <SearchFormValidationErrors
                                    v-if="errors[index]?.length"
                                    :errors="errors[index]"
                                    errorType="airline"
                                    errorMessage="Please select an airline"
                                    class="mt-5 left-0"
                                />
                            </div>
                            <!-- END AIRLINE -->
                            <div
                                class="block w-full @2xl:w-px h-px @2xl:h-8 @2xl:my-auto bg-slate-200"
                            ></div>
                            <!-- START CLASS -->
                            <div
                                class="sm:flex-[1_1_0%] w-full @2xl:h-10 px-4 py-2.5 bg-white justify-start items-center gap-2 flex rounded relative"
                                :class="{
                                    'ring-red-600 ring-1':
                                        errors[index]?.includes('class'),
                                }"
                            >
                                <!-- START ICON -->
                                <div
                                    class="w-5 h-5 justify-center items-center flex text-slate-800"
                                >
                                    <LoungeIcon icon="cabinClass" />
                                </div>
                                <!-- END ICON -->
                                <UiSingleSelect
                                    :options="classes"
                                    v-model:modelValue="localTrips[index].class"
                                    labelKey="name"
                                    valueKey="value"
                                    :label="
                                        localTrips[index].class?.name
                                            ? localTrips[index].class.name
                                            : 'Cabin class access'
                                    "
                                    :disabled="loading"
                                    @changed="() => removeError(index, 'class')"
                                    buttonClass="font-medium whitespace-nowrap rounded inline-flex items-center gap-x-1.5 bg-white px-3 py-2 text-slate-500 font-normal text-sm hover:border-none  focus:border-none focus:outline-none disabled:opacity-50 disabled:text-slate-800 disabled:bg-white disabled:border-slate-300"
                                />
                                <SearchFormValidationErrors
                                    v-if="errors[index]?.length"
                                    :errors="errors[index]"
                                    errorType="class"
                                    errorMessage="Please select a class of service"
                                    class="mt-5 left-0"
                                />
                            </div>
                            <!-- END CLASS -->
                        </div>
                    </template>
                    <template #buttons>
                        <Transition name="fade">
                            <button
                                class="p-2 text-slate-500 absolute -end-10"
                                v-if="localTrips.length > 1"
                                @click="() => removeTrip(index)"
                            >
                                <Icon icon="x" />
                            </button>
                        </Transition>
                        <button
                            v-if="localTrips.length === 1"
                            type="button"
                            @click="handleSubmit"
                            @keyup.enter="handleSubmit"
                            :disabled="loading || hasErrors"
                            class="w-full md:w-auto mt-4 md:mt-0 px-4 py-2.5 bg-orange-600 rounded justify-center items-center gap-2 flex text-white hover:bg-red-400 active:bg-red-400 focus:outline-slate-200 focus:shadow disabled:opacity-50 disabled:bg-orange-600 search-button"
                        >
                            <div class="text-sm font-medium leading-tight">
                                Search
                            </div>
                        </button>
                    </template>
                </SearchBar>
            </span>
        </TransitionGroup>
    </div>
<!-- button container start: -->
    <div
        class="flex flex-col md:flex-row justify-between items-start gap-2.5 transition-all duration-500 button-container"
    >
        <div
            class="w-full flex flex-col md:flex-row justify-between sm:w-auto md:justify-start items-center gap-2 sm:gap-6 md:gap-8"
        >
            <WizardAdvancedOptions class="w-fit" />
            <button
                class="w-fit text-slate-800 rounded justify-center md:justify-start items-center gap-2 px-2 py-1.5 flex disabled:opacity-50 disabled:bg-transparent"
                :disabled="loading"
                @click="addTrip"
            >
                <Icon icon="plus" />
                <div class="text-sm font-medium whitespace-nowrap">
                    Add flight
                </div>
            </button>
        </div>
        <button
            v-if="localTrips.length > 1"
            type="button"
            @click="handleSubmit"
            @keyup.enter="handleSubmit"
            :disabled="loading || hasErrors"
            class="w-full md:w-auto mt-4 md:mt-0 px-4 py-2.5 bg-orange-600 rounded justify-center items-center gap-2 flex text-white hover:bg-red-400 active:bg-red-400 focus:outline-slate-200 focus:shadow disabled:opacity-50 disabled:bg-orange-600 search-button"
        >
            <div class="text-sm font-medium leading-tight">Search</div>
        </button>
    </div>
</template>

<script>
import { computed, ref, watch, onMounted } from 'vue';
import { storeToRefs } from 'pinia';
import { useRouter, useRoute } from 'vue-router';
import { useLoungesStore } from '@/store/lounges';
import FlatPickr from 'vue-flatpickr-component';
import LoungesAutoSuggest from '@/components/tools/lounges/lounges-search/lounges-autosuggest/lounges-autosuggest.vue';
import Icon from '@/components/ui/icon/icon.vue';
import LoungeIcon from '@/components/tools/lounges/partials/lounge-icon/lounge-icon.vue';
import SearchBar from '@/components/ui/inputs/search-bar/search-bar.vue';
import UiSingleSelect from '@/components/ui/inputs/single-select/ui-single-select.vue';
import SearchFormValidationErrors from '@/components/hotels/search/search-form-validation-errors/search-form-validation-errors.vue';
import WizardAdvancedOptions from '@/components/tools/lounges/partials/wizard-advanced-options/wizard-advanced-options.vue';

export default {
    components: {
        FlatPickr,
        Icon,
        LoungeIcon,
        LoungesAutoSuggest,
        SearchBar,
        SearchFormValidationErrors,
        UiSingleSelect,
        WizardAdvancedOptions,
    },
    setup() {
        const loungesStore = useLoungesStore();
        const route = useRoute();
        const { airports, classes, loading, airlines, userFlights } =
            storeToRefs(loungesStore);
        const {
            buildResultsUrl,
            searchWizard,
            setUserFlights,
            parseTripsFromUrl,
        } = loungesStore;
        const router = useRouter();

        const localTrips = ref([
            {
                to: {},
                from: {},
                airline: {},
                class: {},
            },
        ]);

        const errors = ref([]);

        const removeError = (index, error) => {
            if (errors.value[index] && errors.value[index].length) {
                errors.value[index] = errors.value[index].filter(
                    (e) => e !== error
                );
            }
        };

        const handleChange = (val, i, key) => {
            localTrips.value[i][key] = val;
            removeError(i, key);
        };

        const handleSelect = (val, i, key) => {
            localTrips.value[i][key] = val;
            removeError(i, key);
        };

        const addTrip = () => {
            errors.value = [];
            localTrips.value.push({
                to: {},
                from: {},
                airline: {},
                class: {},
            });
        };

        const removeTrip = (i) => {
            errors.value = [];
            localTrips.value.splice(i, 1);
        };

        const hasErrors = computed(() => {
            return errors.value.reduce((sum, curr) => {
                return (sum += curr.length);
            }, 0);
        });

        const validate = () => {
            errors.value = [];

            localTrips.value.forEach((trip, i) => {
                errors.value.push([]);
                if (!trip.to.code) {
                    errors.value[i].push('to');
                }
                if (!trip.from.code) {
                    errors.value[i].push('from');
                }
                if (!trip.airline.code) {
                    errors.value[i].push('airline');
                }
                if (!trip.class.value) {
                    errors.value[i].push('class');
                }
            });
        };
        const handleSubmit = () => {
            validate();
            if (!hasErrors.value) {
                search();
            }
        };

        const search = () => {
            const url = buildResultsUrl(localTrips.value);

            setUserFlights(localTrips.value);

            searchWizard();

            router.push(url);
        };

        watch(
            () => userFlights.value,
            (newValue) => {
                if (
                    JSON.stringify(localTrips.value) !==
                    JSON.stringify(newValue)
                ) {
                    localTrips.value = newValue;
                }
            }
        );

        onMounted(() => {
            const tripsData = parseTripsFromUrl(route.query);
            if (tripsData.flights.length > 0) {
                localTrips.value = tripsData.flights;
            }
        });

        return {
            addTrip,
            airports,
            classes,
            errors,
            handleChange,
            handleSelect,
            handleSubmit,
            hasErrors,
            loading,
            localTrips,
            airlines,
            removeError,
            removeTrip,
        };
    },
};
</script>

<style lang="scss" scoped>
.expand-enter-active,
.expand-leave-active {
    transition: all 0.5s ease;
    overflow: hidden;
}

.expand-enter-from,
.expand-leave-to {
    max-height: 0;
    opacity: 0;
    padding: 0;
    overflow: hidden;
}

.expand-enter-to,
.expand-leave-from {
    max-height: 100px;
    opacity: 1;
    padding: 1rem 0;
}

.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}

.search-button {
    @media (min-width: 768px) and (max-width: 1023px) {
        margin-top: 20px;
    }
}

.search-container {
    @media (min-width: 768px) and (max-width: 1023px) {
        margin-bottom: 20px;
    }
}

.button-container {
    @media (min-width: 768px) and (max-width: 1023px) {
        margin-top: 20px;
    }
}
</style>
